import React from 'react';
import NextImage from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { Button } from '@mantine/core';

import { SplashLogo, LogoTypes } from '~/components/shared/Logos/SplashLogo';
import SplashAppPreview from '~/components/assets/splashAppPreview.png';
import { Text } from '~/domains/design-system/Text';
import dt from '~/domains/common/components/testing.const';
import ContestCard from '~/domains/contest/domains/common/components/ContestCard/ContestCard';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';

import useStyles from './AppDownloadPrompt.styles';

type FullScreenMessageProps = {
  contest?: Contest;
  isMobile: boolean;
  onContinueClick: () => unknown;
  onDownloadClick: () => void;
  subtitle: JSX.Element | string;
  title: JSX.Element | string;
};

export function AppDownloadPrompt({
  contest,
  isMobile,
  onContinueClick,
  onDownloadClick,
  subtitle,
  title,
}: FullScreenMessageProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('common');

  return (
    <div className={classes.container}>
      <div className={classes.subContainer}>
        <SplashLogo type={LogoTypes.PRIMARY_NO_TEXT} size={34} color="1A1C1E" />
        <Text variant="headline-large" weight={800} mt={40}>
          {title}
        </Text>
        <Text variant="body-large" mt={8} maw="400px">
          {subtitle}
        </Text>
        <Button size="md" w={224} mt={40} onClick={onDownloadClick}>
          <Text color="black" weight={700} variant="label-medium">
            {t('pages.appDownload.downloadButton')}
          </Text>
        </Button>
        <Button
          size="sm"
          variant="subtle"
          mt={16}
          onClick={onContinueClick}
          data-test-id={dt.components.appDownloadPrompt.continueUsingWeb}
        >
          <Text color="black" variant="label-small">
            {isMobile
              ? t('pages.appDownload.continueButton')
              : t('pages.appDownload.continueButtonWeb')}
          </Text>
        </Button>
      </div>

      {contest ? (
        <div className={classes.contestContainer}>
          <ContestCard
            contest={contest}
            isContestThumbnailShown
            isCountdownPillEnabled
            isCardMenuVisible={false}
            isStatic
          />
          <Text variant="body-medium" mt={16}>
            {t('pages.appDownload.contestPromoNote')}
          </Text>
        </div>
      ) : (
        <div className={classes.imageContainer}>
          <NextImage
            className={classes.coverImage}
            width={359}
            height={340}
            style={{ marginTop: 4 }}
            alt="Splash App Preview"
            src={SplashAppPreview}
          />
        </div>
      )}
    </div>
  );
}
